/* eslint-disable no-param-reassign */
import { get, isEmpty, isNil } from 'lodash';

import { SUBCAMPAIGN_OFFER_TYPE_OFFER } from 'utils/subcampaign';

import type {
  Campaign,
  Deliverable,
  Subcampaign,
  SubcampaignBulkOfferStore,
  SubcampaignCreateBulkOfferCustomField,
  SubcampaignCreateBulkOfferCustomizedFieldsMap,
  SubcampaignOfferForm,
  SubcampaignSelectedIdsMap
} from 'types';

export const BULK_OFFER_MODAL_MODE_CREATE = 'create';
export const BULK_OFFER_MODAL_MODE_UPDATE = 'update';

export const MIN_SUBCAMPAIGNS_COUNT_FOR_BULK_CREATION = 1;

export const BULK_TYPE_CREATE_OFFERS = 'create_offers';

export function getSelectedSubcampaigns(
  shortlistedSubcampaigns: Array<Subcampaign>,
  selectedIdsMap: SubcampaignSelectedIdsMap
): Array<Subcampaign> {
  return shortlistedSubcampaigns.filter(s => selectedIdsMap[s.id]);
}

export function getBulkOfferSelectedSubcampaigns(
  bulkOfferStore: SubcampaignBulkOfferStore,
  tmpSelectedIdsMap?: SubcampaignSelectedIdsMap | null
): Array<Subcampaign> {
  const selectedIdsMap = tmpSelectedIdsMap || bulkOfferStore.selectedIdsMap;
  return bulkOfferStore.shortlistedSubcampaigns.filter(s => selectedIdsMap[s.id]);
}

export function sumBulkOfferBudgets(
  selectedSubcampaigns: Array<Subcampaign>,
  customizedFieldsByIdMap?: SubcampaignCreateBulkOfferCustomizedFieldsMap | null,
  bulkOfferBudget?: number | null
) {
  const budget = bulkOfferBudget || 0;
  const mapById = customizedFieldsByIdMap || {};

  return selectedSubcampaigns.reduce((sum, subcampaign) => {
    const customizedBudget = mapById[subcampaign.id]?.budget;
    return sum + Number(isNil(customizedBudget) ? budget : customizedBudget);
  }, 0);
}

export function isBulkOfferFieldCustomized(
  fieldName: SubcampaignCreateBulkOfferCustomField,
  bulkOfferStore: SubcampaignBulkOfferStore,
  isCustomizedFully?: boolean
) {
  const selectedSubcampaigns = getBulkOfferSelectedSubcampaigns(bulkOfferStore);

  // $FlowIgnore - method name
  return selectedSubcampaigns[isCustomizedFully ? 'every' : 'some'](subcampaign => {
    const customizedValue = get(bulkOfferStore.customizedFieldsByIdMap, `${subcampaign.id}.${fieldName}`);

    return !isNil(customizedValue);
  });
}

export function isBulkOfferCustomizationValid(
  fieldName: SubcampaignCreateBulkOfferCustomField,
  bulkOfferStore: SubcampaignBulkOfferStore
) {
  const isCustomized = isBulkOfferFieldCustomized(fieldName, bulkOfferStore);

  return !isCustomized || isBulkOfferFieldCustomized(fieldName, bulkOfferStore, true);
}

export function getSelectedCampaigns(
  campaigns: Array<Campaign>,
  selectedIdsMap: {
    [x: number]: boolean
  }
): Array<Campaign> {
  return campaigns.filter(c => selectedIdsMap[c.id]);
}

export const isBulkOfferModalEditable = (
  values: SubcampaignCreateBulkOfferCustomizedFieldsMap,
  fieldName: SubcampaignCreateBulkOfferCustomField
): boolean => {
  const keys = Object.keys(values);

  const fieldsWithNoValue = keys.filter(key => {
    const value = values[key][fieldName];
    return isNil(value) || isEmpty(String(value)) || Number(value) <= 0;
  });

  return fieldsWithNoValue.length === keys.length;
};

export const getCustomOfferFields = (
  subcampaignIds: Array<number>,
  offer: SubcampaignOfferForm,
  deliverables: Array<Deliverable>,
  customizedFieldsByIdMap: SubcampaignCreateBulkOfferCustomizedFieldsMap
): SubcampaignCreateBulkOfferCustomizedFieldsMap => {
  const customizableFields = ['legalRequirements', 'personalMessage'];
  const preparedFieldsByIdMap = {};

  if (offer.type === SUBCAMPAIGN_OFFER_TYPE_OFFER) {
    customizableFields.push('budget');
  }

  // gathering customized values for selected subcampaigns
  subcampaignIds.forEach(subcampaignId => {
    const customizedFields = customizedFieldsByIdMap[subcampaignId] || {};
    const preparedFields = {};
    customizableFields.forEach(fieldName => {
      const value = customizedFields[fieldName];
      // only if a value is present and is different from a value from the bulk offer
      if (!isNil(value) && offer[fieldName] !== value) {
        preparedFields[fieldName] = value;
      }
    });

    preparedFieldsByIdMap[subcampaignId] = preparedFields;
  });

  return preparedFieldsByIdMap;
};
